import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { StoreProductStatus } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import type { RotationOwner } from '../hooks/use-rotation-owners';

/** A status change waiting on the user because rotations own some of its rows. */
export interface RotationChange {
  status: StoreProductStatus;
  owners: RotationOwner[];
  /** Rows the change applies to; one means a row action. */
  total: number;
  /**
   * "All matching": the rows are not loaded, so which a rotation owns is unknown. The dialog
   * warns that some may be, listing the store's rotations (`owners`, counts null).
   */
  matching?: boolean;
}

export interface RotationChangeDialogProps {
  change: RotationChange | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * "Managed by a rotation": a manual publish, unpublish or draft on store products a rotation
 * owns holds only until its next run, which sets the status again (D-181, decision 11), unless
 * the product is excluded in the rotation's pool. Says so, with a link to each rotation, before
 * the change is sent; for "all matching" it says some may be, listing the store's rotations.
 */
export function RotationChangeDialog({ change, onConfirm, onCancel }: RotationChangeDialogProps) {
  const { t } = useTranslation(['storeCatalog', 'common']);
  const format = useFormat();
  const owners = change?.owners ?? [];
  const owned = owners.reduce((sum, owner) => sum + (owner.count ?? 0), 0);
  const matching = change?.matching ?? false;
  const single = !matching && change?.total === 1 ? owners[0] : undefined;

  return (
    <AlertDialog open={change !== null} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('storeCatalog:rotationChange.title')}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-2">
              {matching ? (
                <>
                  <p>
                    {t('storeCatalog:rotationChange.maybe', {
                      count: change?.total ?? 0,
                      total: format.number(change?.total ?? 0),
                    })}{' '}
                    {t('storeCatalog:rotationChange.untilRunMany')}
                  </p>
                  <p>{t('storeCatalog:rotationChange.excludeMany')}</p>
                </>
              ) : single ? (
                <>
                  <p>
                    {t('storeCatalog:rotationChange.one', { name: single.name })}{' '}
                    {single.nextRunAt
                      ? t('storeCatalog:rotationChange.untilRun', {
                          date: format.dateTime(single.nextRunAt),
                        })
                      : t('storeCatalog:rotationChange.untilRunUnknown')}
                  </p>
                  <p>{t('storeCatalog:rotationChange.excludeOne')}</p>
                </>
              ) : (
                <>
                  <p>
                    {t('storeCatalog:rotationChange.many', {
                      count: owned,
                      total: change?.total ?? 0,
                    })}{' '}
                    {t('storeCatalog:rotationChange.untilRunMany')}
                  </p>
                  <p>{t('storeCatalog:rotationChange.excludeMany')}</p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="flex flex-col gap-1 text-sm">
          {owners.map((owner) => (
            <li key={owner.id}>
              <Link
                to="/org/schedules/$scheduleId"
                params={{ scheduleId: owner.id }}
                className="font-medium underline"
              >
                {single ? t('storeCatalog:rotationChange.open', { name: owner.name }) : owner.name}
              </Link>
              {single ? null : owner.count === null ? (
                <span className="text-muted-foreground">
                  {' · '}
                  {owner.nextRunAt
                    ? t('storeCatalog:rotationChange.nextRun', {
                        date: format.dateTime(owner.nextRunAt),
                      })
                    : t('storeCatalog:rotationChange.paused')}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {' · '}
                  {owner.nextRunAt
                    ? t('storeCatalog:rotationChange.ownerRun', {
                        count: owner.count,
                        date: format.dateTime(owner.nextRunAt),
                      })
                    : t('storeCatalog:rotationChange.ownerRunUnknown', { count: owner.count })}
                </span>
              )}
            </li>
          ))}
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>
            {change ? t(`storeCatalog:rotationChange.confirm.${change.status}`) : null}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
